import type { FC, ReactNode } from 'react'
import type { Theme } from '@/types'

import { useRef } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { gsap, useGsap, ScrollTrigger } from '@/lib/gsap'
import { cn } from '@/lib/utils'
import { navLinks } from '@/constants/site'
import { NomaMark } from '@/components/generals/noma-mark'
import { RollText } from '@/components/generals/roll-link'

interface SiteNavProps {
    onMenuToggle: () => void
    menuOpen: boolean
    theme?: Theme
}

export const SiteNav: FC<SiteNavProps> = ({
    onMenuToggle,
    menuOpen,
    theme = 'dark'
}): ReactNode => {
    const scope = useRef<HTMLElement>(null)
    const router = useRouter()
    const dark = theme === 'dark'

    useGsap(() => {
        const bar = scope.current

        if (!bar) {
            return
        }

        /* Rides in just behind the preloader curtain. */
        gsap.fromTo('[data-nav-item]',
            { yPercent: -135, opacity: 0 },
            {
                yPercent: 0,
                opacity: 1,
                duration: 1,
                stagger: 0.07,
                delay: 0.3,
                ease: 'noma'
            }
        )

        /* Hides going down, returns coming back up, and only then fades in its
           backing plate so it never sits on top of the hero. */
        const showHide = ScrollTrigger.create({
            start: 'top top',
            end: 'max',
            onUpdate: (self) => {
                const hidden = self.direction === 1 && self.scroll() > 260

                gsap.to(bar, {
                    yPercent: hidden ? -100 : 0,
                    duration: 0.55,
                    ease: 'noma-soft',
                    overwrite: true
                })
            }
        })

        const plate = ScrollTrigger.create({
            start: 'top+=120 top',
            end: 'max',
            onToggle: (self) => {
                gsap.to('[data-nav-plate]', {
                    opacity: self.isActive ? 1 : 0,
                    duration: 0.5
                })
            }
        })

        return () => {
            showHide.kill()
            plate.kill()
        }
    }, scope, [])

    return (
        <header
            ref={scope}
            className='fixed inset-x-0 top-0 z-[95]'
        >
            <div
                data-nav-plate
                className={cn(
                    'pointer-events-none absolute inset-0 border-b opacity-0 backdrop-blur-md',
                    dark ? 'border-paper/10 bg-ink/80' : 'border-ink/10 bg-paper/85'
                )}
            />

            {/* The links used to be spread across 72% of the bar, which read as
                four items drifting apart rather than as navigation. They sit in
                a centred group now, with the page you are on filled in the
                accent, and the bar closes on a single call to action. */}
            <nav
                className={cn(
                    'relative flex h-[var(--nav-h)] items-center justify-between gap-4 px-5 lg:px-9',
                    dark ? 'text-paper' : 'text-ink'
                )}
                aria-label='Primary'
            >
                <div data-nav-item className='shrink-0'>
                    <Link
                        href='/'
                        data-cursor='link'
                        aria-label='Strength Scaling, home'
                        className='group/roll flex items-center gap-2'
                    >
                        <NomaMark className='size-[24px] transition-colors duration-500 group-hover/roll:text-brand' />

                        <RollText
                            label='Strength Scaling'
                            className='t-display pb-[0.06em] text-[19px] leading-none'
                        />
                    </Link>
                </div>

                {/* The centring lives on the outer element and the entrance
                    tween on the inner one. GSAP writes a whole `transform`, so
                    animating the centred element itself would drop its
                    translate and the group would jump to the middle-left. */}
                <div className='absolute left-1/2 hidden -translate-x-1/2 lg:block'>
                    <div
                        data-nav-item
                        className={cn(
                            'flex items-center gap-1 rounded-full border p-1 backdrop-blur-md',
                            dark ? 'border-paper/15 bg-ink/35' : 'border-ink/12 bg-paper/50'
                        )}
                    >
                        {navLinks.map((link) => {
                            const active = router.pathname === link.href
                                || router.pathname.startsWith(`${link.href}/`)

                            return (
                                <Link
                                    key={link.href}
                                    href={link.href}
                                    data-cursor='link'
                                    aria-current={active ? 'page' : undefined}
                                    className={cn(
                                        'flex items-center gap-1 rounded-full px-4 py-1.5 transition-colors duration-500',
                                        active
                                            ? 'bg-brand text-paper'
                                            : dark
                                                ? 'hover:bg-paper/10'
                                                : 'hover:bg-ink/8'
                                    )}
                                >
                                    <span className='t-cond pb-[0.08em] text-[15px] leading-none'>
                                        {link.label}
                                    </span>

                                    {link.count
                                        ? (
                                            <span
                                                className={cn(
                                                    't-mono nums mt-[1px] text-[9px]',
                                                    active ? 'text-paper/70' : 'text-brand'
                                                )}
                                            >
                                                {link.count}
                                            </span>
                                        )
                                        : null}
                                </Link>
                            )
                        })}
                    </div>
                </div>

                <div data-nav-item className='ml-auto hidden items-center gap-5 lg:flex'>
                    <span className='flex items-center gap-2'>
                        <span className='relative flex size-2'>
                            <span className='absolute inset-0 animate-ping rounded-full bg-brand opacity-60' />

                            <span className='relative block size-2 rounded-full bg-brand' />
                        </span>

                        <span className='t-mono opacity-70'>
                            2 slots open
                        </span>
                    </span>

                    <Link
                        href='/contact'
                        data-cursor='link'
                        className={cn(
                            'group/roll flex items-center gap-2.5 rounded-full px-4 py-2 transition-colors duration-500',
                            dark
                                ? 'bg-paper text-ink hover:bg-brand hover:text-paper'
                                : 'bg-ink text-paper hover:bg-brand'
                        )}
                    >
                        <RollText
                            label='Start a project'
                            className='t-cond pb-[0.08em] text-[14px] leading-none'
                        />

                        <svg viewBox='0 0 24 24' className='size-3 transition-transform duration-500 group-hover/roll:rotate-45' fill='none' aria-hidden='true'>
                            <path d='M5 19L19 5M19 5H8M19 5v11' stroke='currentColor' strokeWidth='2.2' />
                        </svg>
                    </Link>
                </div>

                <button
                    type='button'
                    onClick={onMenuToggle}
                    aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                    aria-expanded={menuOpen}
                    data-nav-item
                    className='ml-auto flex items-center gap-2 lg:hidden'
                >
                    <span className='t-mono'>
                        Menu
                    </span>

                    <span className='grid grid-cols-2 gap-[3px]'>
                        {[0, 1, 2, 3].map((dot) => (
                            <span key={dot} className='block size-[5px] bg-current' />
                        ))}
                    </span>
                </button>
            </nav>
        </header>
    )
}
